import {
  Utensils,
  UtensilsCrossed,
  Package,
  ShoppingBag,
  CircleSlash,
  Settings2,
  FileText,
  Stethoscope,
  HeartPulse,
  History,
  PackagePlus,
  Droplets,
  CalendarPlus,
  CheckCircle2,
  XCircle,
  Scale,
  Syringe,
  Bug,
  Pill,
  Pencil,
  Trash2,
} from 'lucide-react';
import { LOG_TYPES, OPEN_BAG_MODES } from '../../../config/constants';
import { formatGrams, formatTimeAgo, formatList } from '../../../lib/format';

/**
 * Devuelve un string corto con la fecha + hora de una cita ("12 de junio a las 14:30").
 * Acepta Timestamp de Firestore o Date.
 */
const formatAppointmentDateTime = (scheduledAt) => {
  if (!scheduledAt) return '';
  const ms = scheduledAt?.toMillis?.() ?? (scheduledAt instanceof Date ? scheduledAt.getTime() : null);
  if (!ms) return '';
  const d = new Date(ms);
  const date = d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' });
  const time = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `el ${date} a las ${time}`;
};

/**
 * Resuelve los nombres de las pets afectadas en un payload de appointment.
 * Prefiere petNames (snapshot al crear el log) sobre petMap (estado actual).
 */
const resolveAppointmentPetNames = (payload, petMap) => {
  const petIds = Array.isArray(payload.petIds) ? payload.petIds : [];
  const snapshot = Array.isArray(payload.petNames) ? payload.petNames : null;
  if (petIds.length === 0) return 'sus mascotas';
  const names = petIds.map((id, i) => petMap.get(id) ?? snapshot?.[i] ?? null).filter(Boolean);
  if (names.length === 0) return 'sus mascotas';
  return formatList(names);
};

/**
 * Bitácora visual de los eventos del hogar.
 *
 * Cada log se representa con un ícono según el tipo + descripción con
 * sujeto + verbo + objeto explícito en español rioplatense voseante.
 * Resuelve userId → displayName, petId → pet.name, feedingGroupId → group.name
 * usando los 3 maps construidos al render. Si algún lookup falla
 * (miembro que se fue, pet borrada, grupo borrado), cae a fallbacks
 * legibles ('Alguien', 'una mascota', 'un grupo').
 */

/**
 * Arma el objeto { icon, iconClass, text } para un log.
 * Recibe los 3 maps ya construidos (uid → name, petId → name, groupId → name)
 * para resolver referencias en O(1).
 */
const buildEntry = (log, { memberMap, petMap, groupMap, hydrationGroupMap }) => {
  const p = log.payload || {};

  const author = memberMap.get(log.userId) ?? 'Alguien';
  const petName =
    petMap.get(log.petId) ?? p.petName ?? 'una mascota';
  const groupName = groupMap.get(log.feedingGroupId) ?? 'un grupo';
  const dispName = p.dispenserName ?? 'un dispenser';
  const hydrationGroupName =
    hydrationGroupMap.get(log.hydrationGroupId) ?? p.groupName ?? 'un balde';

  switch (log.type) {
    case LOG_TYPES.FEEDING: {
      // Stage 2: si hay dispenserName, el feed vino de un dispenser específico.
      // Si no, vino directo de la bolsa principal (modo sin dispenser).
      const source = p.dispenserName
        ? `del dispenser '${p.dispenserName}'`
        : 'de la bolsa';
      return {
        icon: Utensils,
        iconClass: 'text-status-green bg-emerald-50',
        text: `${author} sirvió ${formatGrams(p.amountGr)} ${source} a ${petName}`,
      };
    }
    case LOG_TYPES.MANUAL_FEEDING:
      return {
        icon: UtensilsCrossed,
        iconClass: 'text-amber-700 bg-amber-50',
        text: `${author} alimentó a ${petName} sin descontar stock`,
      };
    case LOG_TYPES.REFILL: {
      // Tres variantes según wasEmpty / isPartialRefill / closedCycleId:
      //  1) Refill que cerró un ciclo (wasEmpty=true, closedCycleId set)
      //  2) Refill parcial (acumuló en un ciclo abierto)
      //  3) Refill normal (primero del ciclo, o no cerró nada)
      if (p.closedCycleId) {
        const feedings = p.closedCycleFeedings ?? 0;
        const outlierTag = p.closedCycleWasOutlier ? ' (ciclo atípico)' : '';
        return {
          icon: Package,
          iconClass: 'text-brand-700 bg-brand-50',
          text: `${author} cargó ${formatGrams(p.amountGr)} al dispenser '${dispName}' y cerró el ciclo (${feedings} comida${feedings === 1 ? '' : 's'})${outlierTag}`,
        };
      }
      if (p.isPartialRefill) {
        return {
          icon: Package,
          iconClass: 'text-brand-700 bg-brand-50',
          text: `${author} agregó ${formatGrams(p.amountGr)} al dispenser '${dispName}' (rellenado parcial)`,
        };
      }
      return {
        icon: Package,
        iconClass: 'text-brand-700 bg-brand-50',
        text: `${author} cargó ${formatGrams(p.amountGr)} de la bolsa al dispenser '${dispName}'`,
      };
    }
    case LOG_TYPES.MAIN_STOCK_ADD:
      return {
        icon: ShoppingBag,
        iconClass: 'text-brand-700 bg-brand-50',
        text: `${author} agregó ${formatGrams(p.amountGr)} a la bolsa principal de ${groupName}`,
      };
    case LOG_TYPES.BAGS_BOUGHT: {
      const count = p.count ?? 0;
      const cap = p.bagCapacityGr ? ` de ${formatGrams(p.bagCapacityGr)}` : '';
      return {
        icon: PackagePlus,
        iconClass: 'text-brand-700 bg-brand-50',
        text: `${author} sumó ${count} bolsa${count === 1 ? '' : 's'}${cap} a la reserva de ${groupName}`,
      };
    }
    case LOG_TYPES.BAG_OPENED: {
      const cap = p.bagCapacityGr ? formatGrams(p.bagCapacityGr) : 'una bolsa';
      let detail;
      if (p.mode === OPEN_BAG_MODES.PREVIOUS_EMPTY) {
        detail = p.mainDiscardedGr > 0
          ? `descartó ${formatGrams(p.mainDiscardedGr)} fantasma`
          : 'la anterior estaba vacía';
      } else {
        detail = 'sumada al stock previo';
      }
      return {
        icon: ShoppingBag,
        iconClass: 'text-emerald-700 bg-emerald-50',
        text: `${author} abrió una bolsa nueva en ${groupName} (${cap}, ${detail})`,
      };
    }
    case LOG_TYPES.CONFIRM_EMPTY: {
      const tag = p.isOutlier ? ' (ciclo atípico, no afecta estimaciones)' : '';
      return {
        icon: CircleSlash,
        iconClass: 'text-slate-700 bg-slate-100',
        text: `${author} marcó el dispenser '${dispName}' como vacío en ${groupName}${tag}`,
      };
    }
    case LOG_TYPES.STOCK_ADJUSTMENT:
      // Caso especial: ajuste hecho al crear la mascota.
      if (p.reason === 'pet_creation') {
        return {
          icon: ShoppingBag,
          iconClass: 'text-slate-700 bg-slate-100',
          text: `Stock inicial cargado al crear ${petName}`,
        };
      }
      return {
        icon: Settings2,
        iconClass: 'text-slate-700 bg-slate-100',
        text: p.dispenserName
          ? `${author} ajustó el stock del dispenser '${p.dispenserName}' en ${groupName}`
          : `${author} ajustó el stock de la bolsa de ${groupName}`,
      };
    case LOG_TYPES.WATERING: {
      const names = Array.isArray(p.affectedPetNames) ? p.affectedPetNames : [];
      const list = names.length > 0 ? formatList(names) : 'sus mascotas';
      const verb = names.length === 1 ? 'tomó' : 'tomaron';
      return {
        icon: Droplets,
        iconClass: 'text-sky-700 bg-sky-50',
        text: `${author} le dio agua al balde '${hydrationGroupName}' — ${verb} ${list}`,
      };
    }
    case LOG_TYPES.NOTE: {
      if (p.reason === 'hydration_group_created') {
        return {
          icon: Droplets,
          iconClass: 'text-sky-700 bg-sky-50',
          text: `${author} creó el balde de agua '${p.groupName ?? hydrationGroupName}'`,
        };
      }
      if (p.reason === 'hydration_group_renamed') {
        return {
          icon: Droplets,
          iconClass: 'text-sky-700 bg-sky-50',
          text: `${author} renombró el balde de '${p.previousName ?? '—'}' a '${p.newName ?? hydrationGroupName}'`,
        };
      }
      if (p.reason === 'hydration_group_interval_changed') {
        return {
          icon: Droplets,
          iconClass: 'text-sky-700 bg-sky-50',
          text: `${author} cambió la frecuencia del balde '${p.groupName ?? hydrationGroupName}' a cada ${p.newInterval}h`,
        };
      }
      if (p.reason === 'hydration_group_deleted') {
        return {
          icon: Droplets,
          iconClass: 'text-slate-700 bg-slate-100',
          text: `${author} eliminó el balde de agua '${p.groupName ?? hydrationGroupName}'`,
        };
      }
      if (p.reason === 'hydration_enabled_for_pet') {
        return {
          icon: Droplets,
          iconClass: 'text-sky-700 bg-sky-50',
          text: `${author} activó el tracking de agua para ${p.petName ?? petName}`,
        };
      }
      if (p.reason === 'hydration_disabled_for_pet') {
        return {
          icon: Droplets,
          iconClass: 'text-slate-700 bg-slate-100',
          text: `${author} desactivó el tracking de agua para ${p.petName ?? petName}`,
        };
      }
      if (p.reason === 'hydration_group_changed_for_pet') {
        return {
          icon: Droplets,
          iconClass: 'text-sky-700 bg-sky-50',
          text: `${author} cambió el balde de agua de ${p.petName ?? petName}`,
        };
      }
      if (p.reason === 'pet_deleted') {
        return {
          icon: FileText,
          iconClass: 'text-slate-700 bg-slate-100',
          text: `${author} eliminó a ${p.petName ?? 'una mascota'}`,
        };
      }
      if (p.reason === 'pet_moved_to_group') {
        // Resolvemos los dos grupos contra el groupMap; fallback "otro grupo"
        // si alguno no resuelve (grupo borrado / nunca existió en la lista activa).
        const fromName = groupMap.get(p.fromGroupId) ?? 'otro grupo';
        const toName = groupMap.get(p.toGroupId) ?? 'otro grupo';
        const movedName = p.petName ?? 'una mascota';
        return {
          icon: FileText,
          iconClass: 'text-slate-700 bg-slate-100',
          text: `${author} movió a ${movedName} de ${fromName} a ${toName}`,
        };
      }
      if (p.reason === 'dispenser_added') {
        const dn = p.dispenserName ?? 'un dispenser';
        return {
          icon: Package,
          iconClass: 'text-brand-700 bg-brand-50',
          text: `${author} agregó el dispenser '${dn}' a ${groupName}`,
        };
      }
      if (p.reason === 'dispenser_removed') {
        const dn = p.dispenserName ?? 'un dispenser';
        const moved = p.stockMovedGr > 0
          ? ` (${formatGrams(p.stockMovedGr)} pasaron a la bolsa)`
          : '';
        return {
          icon: Package,
          iconClass: 'text-slate-700 bg-slate-100',
          text: `${author} quitó el dispenser '${dn}' de ${groupName}${moved}`,
        };
      }
      return {
        icon: FileText,
        iconClass: 'text-slate-700 bg-slate-100',
        text: p.note ? `${author}: ${p.note}` : `${author} agregó una nota`,
      };
    }
    case LOG_TYPES.APPOINTMENT_CREATED: {
      const petsText = resolveAppointmentPetNames(p, petMap);
      const when = formatAppointmentDateTime(p.scheduledAt);
      const title = p.title ? `"${p.title}"` : 'una cita';
      return {
        icon: CalendarPlus,
        iconClass: 'text-purple-700 bg-purple-50',
        text: `${author} agendó ${title} para ${petsText} ${when}`.trim(),
      };
    }
    case LOG_TYPES.APPOINTMENT_COMPLETED: {
      const petsText = resolveAppointmentPetNames(p, petMap);
      const title = p.title ? `"${p.title}"` : 'una cita';
      return {
        icon: CheckCircle2,
        iconClass: 'text-emerald-700 bg-emerald-50',
        text: `${author} marcó como hecha ${title} (${petsText})`,
      };
    }
    case LOG_TYPES.APPOINTMENT_CANCELLED: {
      const petsText = resolveAppointmentPetNames(p, petMap);
      const title = p.title ? `"${p.title}"` : 'una cita';
      return {
        icon: XCircle,
        iconClass: 'text-slate-700 bg-slate-100',
        text: `${author} canceló ${title} (${petsText})`,
      };
    }
    case LOG_TYPES.VET_VISIT:
      return {
        icon: Stethoscope,
        iconClass: 'text-sky-700 bg-sky-50',
        text: `${author} registró una visita al veterinario para ${petName}`,
      };
    case LOG_TYPES.HEALTH: {
      // Discriminator por payload.reason: el mismo LOG_TYPES.HEALTH cubre
      // creación, edición y eliminación de healthRecords. Snapshot del title
      // en payload — la observación puede estar soft-deleted y el log
      // tiene que seguir siendo legible.
      const recordPetName = p.petName ?? petName;
      const title = p.title ?? '';

      if (p.reason === 'health_record_edited') {
        return {
          icon: Pencil,
          iconClass: 'text-amber-700 bg-amber-50',
          text: title
            ? `${author} actualizó la observación "${title}" de ${recordPetName}`
            : `${author} actualizó una observación de ${recordPetName}`,
        };
      }
      if (p.reason === 'health_record_deleted') {
        return {
          icon: Trash2,
          iconClass: 'text-slate-700 bg-slate-100',
          text: title
            ? `${author} eliminó la observación "${title}" de ${recordPetName}`
            : `${author} eliminó una observación de ${recordPetName}`,
        };
      }
      // Default: health_record_created (incluye logs viejos sin reason).
      const recordType = p.recordType;
      if (recordType === 'weight' && p.weightKg != null) {
        const kg = Number.isInteger(p.weightKg)
          ? `${p.weightKg}`
          : Number(p.weightKg).toFixed(2).replace(/0$/, '').replace(/\.$/, '');
        return {
          icon: Scale,
          iconClass: 'text-emerald-700 bg-emerald-50',
          text: `${author} anotó el peso de ${recordPetName}: ${kg} kg`,
        };
      }
      if (recordType === 'vaccine') {
        return {
          icon: Syringe,
          iconClass: 'text-sky-700 bg-sky-50',
          text: p.productName
            ? `${author} registró vacuna "${p.productName}" para ${recordPetName}`
            : `${author} registró una vacuna para ${recordPetName}${title ? `: "${title}"` : ''}`,
        };
      }
      if (recordType === 'deworming') {
        return {
          icon: Bug,
          iconClass: 'text-amber-700 bg-amber-50',
          text: p.productName
            ? `${author} le dio antiparasitario a ${recordPetName} ("${p.productName}")`
            : `${author} registró antiparasitario para ${recordPetName}${title ? `: "${title}"` : ''}`,
        };
      }
      if (recordType === 'medication') {
        return {
          icon: Pill,
          iconClass: 'text-purple-700 bg-purple-50',
          text: `${author} anotó medicación de ${recordPetName}${title ? `: "${title}"` : ''}`,
        };
      }
      if (recordType === 'injury') {
        return {
          icon: HeartPulse,
          iconClass: 'text-rose-700 bg-rose-50',
          text: `${author} registró una lesión de ${recordPetName}${title ? `: "${title}"` : ''}`,
        };
      }
      if (recordType === 'checkup') {
        return {
          icon: Stethoscope,
          iconClass: 'text-brand-700 bg-brand-50',
          text: `${author} registró un control de ${recordPetName}${title ? `: "${title}"` : ''}`,
        };
      }
      // 'note' o sin recordType (logs históricos pre-Sprint-3).
      return {
        icon: HeartPulse,
        iconClass: 'text-rose-700 bg-rose-50',
        text: title
          ? `${author} anotó "${title}" sobre ${recordPetName}`
          : `${author} registró una observación de salud sobre ${recordPetName}`,
      };
    }
    default:
      return {
        icon: History,
        iconClass: 'text-slate-700 bg-slate-100',
        text: `${author} registró un evento`,
      };
  }
};

const LogItem = ({ log, maps }) => {
  const entry = buildEntry(log, maps);
  const Icon = entry.icon;
  const ts = log.timestamp?.toMillis?.() ?? null;

  return (
    <li className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-b-0">
      <div className={['flex h-8 w-8 shrink-0 items-center justify-center rounded-full', entry.iconClass].join(' ')}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-900 leading-snug">{entry.text}</p>
        {ts && (
          <p className="text-xs text-slate-400 mt-0.5">{formatTimeAgo(ts)}</p>
        )}
      </div>
    </li>
  );
};

/**
 * @param {Array} logs - Logs ordenados por timestamp desc
 * @param {Array} members - Perfiles de los miembros del hogar (para resolver userId → displayName)
 * @param {Array} pets - Mascotas del hogar (para resolver petId → name)
 * @param {Array} groups - Grupos de alimentación del hogar (para resolver feedingGroupId → name)
 * @param {Array} hydrationGroups - Baldes de agua del hogar (para resolver hydrationGroupId → name)
 */
const LogTimeline = ({ logs, members = [], pets = [], groups = [], hydrationGroups = [] }) => {
  if (!logs || logs.length === 0) {
    return (
      <div className="py-8 text-center">
        <History className="mx-auto h-8 w-8 text-slate-300" aria-hidden="true" />
        <p className="mt-2 text-sm text-slate-500">Sin actividad todavía.</p>
      </div>
    );
  }

  // 4 maps O(1) para resolver referencias. Sin useMemo: N chico (<100 docs típico
  // por hogar), el costo de rebuild es despreciable y menor al del memo.
  const memberMap = new Map(members.map((m) => [m.id ?? m.uid, m.displayName]));
  const petMap = new Map(pets.map((p) => [p.id, p.name]));
  const groupMap = new Map(groups.map((g) => [g.id, g.name]));
  const hydrationGroupMap = new Map(hydrationGroups.map((g) => [g.id, g.name]));
  const maps = { memberMap, petMap, groupMap, hydrationGroupMap };

  return (
    <ul className="flex flex-col">
      {logs.map((log) => (
        <LogItem key={log.id} log={log} maps={maps} />
      ))}
    </ul>
  );
};

export default LogTimeline;
